import React, { useState } from 'react';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import useMobile from '../hooks/useMobile';

const QuestionCard = ({ question, index, selectedAnswers, onAnswerChange }) => {
    const [showAnswer, setShowAnswer] = useState(false);
    const isMultiple = question.assessment_type === 'multiple';
    const isMobile = useMobile();
    
    // Get current selected answers for this question
    const currentAnswers = (selectedAnswers && selectedAnswers[question.id]) ? selectedAnswers[question.id] : [];

    const getAnswerLabel = (answerIndex) => {
        return String.fromCharCode(65 + answerIndex); // A, B, C, D...
    };

    const isCorrectAnswer = (answerIndex) => {
        // Check if this answer index is in the correct_response array
        // correct_response can be ['a', 'b'] or [0, 1] format
        return question.correct_response.some(r => {
            if (typeof r === 'string') {
                const index = r.toLowerCase().charCodeAt(0) - 97;
                return index === answerIndex;
            }
            return r === answerIndex;
        });
    };

    return (
        <Card className="mb-4">
            <Card.Body>
                <Card.Title>
                    Question {index + 1}
                </Card.Title>
                
                <div 
                    className="mb-3"
                    dangerouslySetInnerHTML={{ __html: question.question }}
                />

                <Form>
                    {question.answers.map((answer, answerIndex) => {
                        const label = getAnswerLabel(answerIndex);
                        const isCorrect = isCorrectAnswer(answerIndex);
                        const isSelected = currentAnswers.includes(answerIndex);
                        
                        const handleAnswerChange = (checked) => {
                            if (onAnswerChange) {
                                if (isMultiple) {
                                    // Multiple choice: add/remove from array
                                    if (checked) {
                                        onAnswerChange(question.id, [...currentAnswers, answerIndex]);
                                    } else {
                                        onAnswerChange(question.id, currentAnswers.filter(a => a !== answerIndex));
                                    }
                                } else {
                                    // Single choice: replace array
                                    onAnswerChange(question.id, checked ? [answerIndex] : []);
                                }
                            }
                        };
                        
                        return (
                            <div key={answerIndex} className="mb-2">
                                <Form.Check
                                    type={isMultiple ? 'checkbox' : 'radio'}
                                    id={`q${question.id}-a${answerIndex}`}
                                    name={isMultiple ? undefined : `q${question.id}`}
                                    checked={isSelected}
                                    onChange={(e) => handleAnswerChange(e.target.checked)}
                                    label={
                                        <span>
                                            <strong>{label}.</strong>{' '}
                                            {answer.includes('</') || answer.includes('<') ? (
                                                <span dangerouslySetInnerHTML={{ __html: answer.replace(/^[A-Z]\.\s*/, '') }} />
                                            ) : (
                                                answer.replace(/^[A-Z]\.\s*/, '')
                                            )}
                                            {showAnswer && isCorrect && (
                                                <span className="badge bg-success ms-2">Correct</span>
                                            )}
                                        </span>
                                    }
                                    disabled={showAnswer}
                                />
                            </div>
                        );
                    })}
                </Form>

                {/* Show correct answers when showAnswer is true (always visible) */}
                {showAnswer && (
                    <Alert variant="info" className="mt-3">
                        <strong>Correct Answer(s):</strong>{' '}
                        {question.correct_response
                            .map(r => {
                                // Handle both string and number formats
                                const index = typeof r === 'string' 
                                    ? r.toLowerCase().charCodeAt(0) - 97 
                                    : r;
                                return getAnswerLabel(index).toUpperCase();
                            })
                            .join(', ')}
                    </Alert>
                )}

                {/* Show explanation only on desktop (not mobile/tablet) */}
                {showAnswer && !isMobile && (
                    <Alert variant="info" className="mt-3">
                        <strong>Explanation:</strong>{' '}
                        {question.explanation || 'No explanation provided.'}
                    </Alert>
                )}

                {/* "Show Answer" button is ALWAYS visible (mobile and desktop) */}
                <Button 
                    variant={showAnswer ? 'secondary' : 'primary'}
                    onClick={() => setShowAnswer(!showAnswer)}
                    className="mt-3"
                >
                    {showAnswer ? 'Hide Answer' : 'Show Answer'}
                </Button>
            </Card.Body>
        </Card>
    );
};

export default QuestionCard;

